import { FC, useEffect, useState } from 'react';
import { Check, Cpu, Loader2, X } from 'lucide-react';
import { getOllamaStatus, getOllamaModels, warmOllama, OllamaStatus, OllamaModelsResponse } from '@/api/desktop';
import { runLocalAISetup, SetupFail } from './modelManagerSetup';
import { useAuth } from '@/hooks/useAuth';

// 桌面端模型管家引导 + 常驻模型预热
// - 本地 AI 未就绪且未关闭过提示 → 首次启动横幅，「一键装好」直接在横幅里跑全链
//   （装 Ollama→拉起→基础双模型免费通道；09-05 拍板真一键，不再只跳设置页）
// - 已就绪 → 每会话预热一次常驻小模型/嵌入模型（Ctrl+Shift+J 唤起秒回）

const DISMISS_KEY = 'modelManagerBannerDismissed';

type Phase = 'idle' | 'working' | 'done' | 'pro-install' | 'error';

const ModelManagerBanner: FC = () => {
  const isDesktop = !!window.psbDesktop?.isDesktop;
  const { isLoggedIn } = useAuth();
  const [show, setShow] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [ctx, setCtx] = useState<{ status: OllamaStatus; catalog: OllamaModelsResponse } | null>(null);

  useEffect(() => {
    if (!isDesktop || !isLoggedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const st = await getOllamaStatus();
        if (cancelled) return;
        if (st.ready) {
          // 就绪：每会话预热一次常驻模型，唤起秒回
          if (!sessionStorage.getItem('ollamaWarmed')) {
            sessionStorage.setItem('ollamaWarmed', '1');
            warmOllama().catch(() => {});
          }
        } else if (localStorage.getItem(DISMISS_KEY) !== '1') {
          const gb = (await window.psbDesktop?.getMemoryGB?.()) || 0;
          const catalog = await getOllamaModels(gb).catch(() => null);
          if (cancelled) return;
          if (catalog) setCtx({ status: st, catalog });
          setShow(true);
        }
      } catch {
        // 后端未就绪等场景静默
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDesktop, isLoggedIn]);

  const run = async () => {
    if (!ctx) return;
    setPhase('working');
    setError('');
    try {
      await runLocalAISetup({
        status: ctx.status,
        catalog: ctx.catalog,
        onProgress: (p) => setProgress(p.percent !== null ? `${p.message} ${p.percent}%` : p.message),
      });
      setPhase('done');
      warmOllama().catch(() => {});
    } catch (e: any) {
      if (e instanceof SetupFail && e.kind === 'pro-install') {
        setPhase('pro-install');
      } else {
        setError(e?.message || '安装失败');
        setPhase('error');
      }
    }
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-24 right-4 z-50 w-80 glass rounded-[2px] border border-info/30 p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-[2px] bg-info/10 flex items-center justify-center shrink-0">
          <Cpu size={16} className="text-info" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">装好本地 AI，免费</p>
          {phase === 'idle' && (
            <>
              <p className="text-xs text-text-muted mt-1 leading-relaxed">
                数据不出机器：一键装好 Ollama 与基础模型（常驻小模型+嵌入模型，免费通道），随开随用；大模型分档推荐见模型管家。
              </p>
              <div className="flex items-center gap-3 mt-2.5">
                <button
                  onClick={run}
                  disabled={!ctx}
                  className="px-3 py-1.5 rounded-[2px] bg-info/15 text-info text-xs hover:bg-info/25 transition-all disabled:opacity-50"
                >
                  一键装好
                </button>
                <button
                  onClick={() => setShow(false)}
                  className="text-xs text-text-muted hover:text-text-secondary"
                >
                  下次再说
                </button>
              </div>
            </>
          )}
          {phase === 'working' && (
            <p className="text-xs text-text-secondary mt-1.5 flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin shrink-0" />
              <span className="truncate">{progress || '正在准备…'}</span>
            </p>
          )}
          {phase === 'done' && (
            <p className="text-xs text-success mt-1.5 flex items-center gap-1.5">
              <Check size={13} /> 本地 AI 已就绪：对话、检索、自动打标全部离线可用。
            </p>
          )}
          {phase === 'error' && (
            <p className="text-xs text-danger mt-1.5">
              {error}（网络不佳可重试，或到 设置→AI 的模型管家里手动执行）
            </p>
          )}
          {phase === 'pro-install' && (
            <p className="text-xs text-text-muted mt-1 leading-relaxed">
              暂时无法自动安装 Ollama；可手动前往
              <span className="text-info"> ollama.com/download </span>
              安装后重启本应用，基础模型照样一键免费拉取。
            </p>
          )}
        </div>
        <button
          onClick={() => {
            localStorage.setItem(DISMISS_KEY, '1');
            setShow(false);
          }}
          title="不再提示"
          className="text-text-muted hover:text-text-secondary shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default ModelManagerBanner;
